/** Shared types. Field names follow the original single-file tool so saved sheets stay compatible. */

export type Lang = "zh" | "en";

/** Bilingual text picker — same rule as the original `L(e, z)`: Chinese falls back to English. */
export const L = (lang: Lang, en: string, zh?: string): string => (lang === "zh" ? zh || en : en);

/* ---------------- history data ---------------- */

export interface Stats {
  n?: number;
  cm_med?: number | null;
  fob_med?: number;
  fob_p25?: number;
  fob_p75?: number;
  mat_med?: number;
  markup?: number;
}

/** Price pool. Fabric recs: [px, code, composition, sc, desc]; trim recs: [px, code, desc]. */
export interface Pool {
  n?: number;
  unit?: string;
  recs: unknown[][];
}

export interface City { city: string; cost_per_min_rmb: number; factory_mult: number; country: string }
export interface MoqBand { min_qty: number; eff: number; mult: number }
export interface CmEngine { rmb_usd: number; default_margin: number; cities: City[]; moq_curve: MoqBand[] }

/** One searchable SC with a BOM (the original `IDX` entry). */
export interface IndexItem {
  sc: string;
  sub: string;
  cat: string;
  gender: string;
  cust: string;
  tier: string | number;
  fob: number;
  mat: number;
  cm: number;
  qty: number;
  co: string;
  fac: string;
  style: string;
  tt: string;
  /** ex-factory date of the order (JF 2026-10-13: shown on the historical style; not in the data yet — IT fills it from the warehouse) */
  exFty?: string;
  desc: string;
  img: string;
  nc: number;
  hasF: boolean;
  okT: boolean;
  ok: boolean;
  hay: string;
}

/** BOM component: [code, usage, unit, price_usd, line_usd, desc, 'fabric'|'trim', type, placement, main, section, supplier_article] */
export type BomComp = unknown[];

/** Public historical BOM: descriptions only. No line prices, usages, amounts or quotes. */
export interface BomText {
  code: string; unit: string; description: string; kind: string; type: string; placement: string; main: string; supplierArticle: string;
}

/**
 * Everything the engine reads from history. Shape mirrors the original `__D` plus the search index.
 * Object key order matters in a few places (sub2cat, fabSub[sub], trimGlobal) — adapters must preserve it.
 */
export interface History {
  sub: Record<string, Record<string, Stats>>;
  cat: Record<string, Stats>;
  /** sub-category → category, in the original SUB2CAT order */
  sub2cat: Record<string, string>;
  fabSub: Record<string, Record<string, Pool>>;
  fabGlobal: Record<string, Pool>;
  trimSub: Record<string, Record<string, Pool>>;
  trimGlobal: Record<string, Pool>;
  usage: { sub: Record<string, { m: number }>; cat: Record<string, { m: number }> };
  subSam: Record<string, number>;
  webSam: Record<string, number>;
  subTrims: Record<string, [string, number][]>;
  cmEngine: CmEngine;
  /** searchable SCs, already in display order (see compareIndex) */
  index: IndexItem[];
}

/* ---------------- cost sheet ---------------- */

export type SrcType = "ai" | "hist" | "man";
export interface Src { t: SrcType; d: string }

/** One line at one quantity of a tiered quote. */
export interface TierLine { px: string; yld: string; why: string; /** the AI gave no number for this quantity — the sheet's own was copied */ none?: boolean; /** px typed in RMB */ cny?: string }
/** One quantity's per-piece CM parts (key = part); `cny` = parts typed in RMB */
export type TierProc = Partial<Record<CmPartKey, string>> & { cny?: Partial<Record<CmPartKey, string>> };
/** A tiered quote on a sheet: quantities, cut-make-pack and processes per quantity (key = qty). */
export interface SheetTiers {
  qtys: number[];
  cm: Record<string, { px: string; why: string; none?: boolean }>;
  /** per quantity: every per-piece CM part (cut / sew / pack / wash / emb / other); `cny` = the RMB amounts typed (JF 2026-10-13) */
  proc?: Record<string, TierProc>;
  note?: Record<string, string>;
  /** the quantity the cost sheet above shows (its lines / CM / qty are that tier, edits go to it) — JF 2026-10-12 */
  active?: number;
  /** quote coefficients per quantity (key = qty); missing = the sheet's — JF 2026-10-13 */
  coef?: Record<string, QCoef>;
}
/** The AI's judgement for one quantity (same order as fabrics / trims of the reply). */
export interface PriceTier {
  qty?: number;
  /** only the lines that change, by i (position in the main list); older replies: every line in order */
  fabrics?: { i?: number; px?: number; yield?: number; why?: string }[];
  trims?: { i?: number; px?: number; yield?: number; why?: string }[];
  cm_base?: number; cm_why?: string;
  /** cm_base split into cutting / sewing / packing (JF 2026-10-13) */
  cm_split?: { cut?: number; sew?: number; pack?: number };
  process?: { wash?: number; emb?: number; other?: number };
  note?: string;
}

export interface LineRow {
  name: string;
  pos: string;
  unit?: string; // fabric only
  supp?: string; // trim only
  qual: string;
  oem: string;
  cat: string;
  px: string;
  yld: string;
  rem: string;
  src: { name: Src | null; px: Src | null; yld: Src | null };
  q: { px: string | number | null; fin: string | number | null; yld: string | number | null };
  /** tiered quote: the AI's unit price / yield for each quantity (key = qty), with its reason (JF 2026-10-12) */
  tq?: Record<string, TierLine>;
  /** the unit price as typed in RMB; px is then cny ÷ sheet.fx (JF 2026-10-13) */
  cny?: string;
  flag: string;
}

export type LineKind = "fab" | "trim";
export type AddKey = "wash" | "emb" | "trans" | "sample" | "handle" | "drop";
/** The other Additional rows, whose amount lives in sheet.test / insp / setup (JF 2026-10-13: one table, one way) */
export type ExtraKey = "test" | "insp" | "setup";
export type Per = "pc" | "order";
/** One other upcharge: what it is, the amount per piece or for the whole order, and whether it counts as profit (GP). */
export interface Upcharge { desc: string; amount: string; per: Per; gp: boolean; /** amount as typed in RMB */ cny?: string }
/**
 * Parts of the CM = everything paid to the garment factory (JF 2026-10-13): cutting, sewing, packing, washing,
 * embroidery / print, other processes, customs & document fees. CM = their per-piece sum.
 * "cmt" = cut-make-pack in one figure, on sheets made before the split (kept as it was).
 */
export type CmPartKey = "cut" | "sew" | "pack" | "wash" | "emb" | "other" | "docs" | "cmt";
/**
 * mult = this row's own CM factor (empty = the sheet's CM factor); per = per piece (default) or a whole-order amount spread
 * over the quantity; cny = the amount as typed in RMB (px = cny ÷ sheet.fx) — JF 2026-10-13
 */
export interface CmPart { px: string; desc: string; mult?: string; per?: Per; cny?: string }
/** as stored (older sheets have only some parts) */
export type CmParts = Partial<Record<CmPartKey, CmPart>>;
/** every part, empty where not set — what cmPartsOf returns */
export type AllCmParts = Record<CmPartKey, CmPart>;
/** A CM row the AM added (JF 2026-10-13): own name, description, amount and optionally its own factor. */
export interface CmMore extends CmPart { name: string }

export interface Header {
  customer: string; date: string; season: string; style: string; name: string; factory: string;
  co: string; content: string; mill: string; article: string; width: string; qty: string;
}

/** Structured CM cross-check (replaces the original HTML string `cm.chk`; render with renderCmCheck). */
export interface CmCheck {
  ai: { cm: number; rush: number; reason_en: string; reason_zh: string };
  hist: { sub: string; cat: string; level: "sub" | "cat"; cm_med: number; n: number | undefined } | null;
  eng: { sam: number; city: string; moq: number; usd: number } | null;
  far: boolean;
  /** 偏差 D9: only set when the size range is unusual (≤5, >8, or extended sizes) — a reminder, not a coefficient */
  sizes?: { count: number; band: "few" | "normal" | "many"; extended: boolean } | null;
}

/**
 * Quote coefficients of one sheet (偏差 D8, company template). Fractions, except cmMult (a factor).
 * Actual = Px × (1 + finActual) × Yield; Quoted = Px × (1 + px%) × (1 + fin) × Yield × (1 + yield%); Quoted CM = CM × cmMult.
 */
export interface QCoef {
  finActual: number; // finance inside the actual cost (company template and the v2 tool: 0.06)
  fin: number;       // finance on quoted lines
  fabPx: number; fabYld: number; trimPx: number; trimYld: number;
  add: number;       // additional costs markup
  cmMult: number;    // quoted CM = CM × cmMult
  tariff?: number;   // default duty % for DDP on a new sheet
}

export interface Sheet {
  /** RMB per USD for this sheet (JF 2026-10-13, default 6.71). Amounts are kept in USD; the ones typed in RMB keep that too. */
  fx?: number;
  /** absent = sheet made before D8 → the v2 formulas (LEGACY_QCOEF) keep its numbers unchanged */
  qcoef?: QCoef;
  h: Header;
  fab: LineRow[];
  trim: LineRow[];
  /** v = the CM total; when `parts` is set, v is their sum (sheets without parts: the whole CM is cut-make-pack) */
  cm: { v: string; note: string; src: Src | null; q: string | number | null; chk: string; check?: CmCheck | null; parts?: CmParts; more?: CmMore[] };
  /** px = the amount; per "order" = a whole-order total spread over the order qty (default "pc") — JF 2026-10-13 */
  add: Record<AddKey, { desc: string; px: string; q: string | number | null; per?: Per; /** px as typed in RMB */ cny?: string }>;
  test: string;
  insp: string;
  /** one-off development / set-up total $ (pattern, screens, embroidery digitising…), spread over the order qty (JF 2026-10-12) */
  setup?: string;
  /** testing / inspection / development rows: description, per piece or whole order (default "order"), quoted per piece typed by hand */
  extra?: Partial<Record<ExtraKey, { desc?: string; per?: Per; q?: string | number | null; /** the amount as typed in RMB */ cny?: string }>>;
  /** tiered quote — the AI priced every quantity (JF 2026-10-12); lines carry their own numbers in LineRow.tq. See tiers.ts */
  tiers?: SheetTiers;
  quote: boolean;
  /** Other upcharge NOT counted in GP (company template's row; e.g. collected on someone's behalf) */
  other: string | number;
  /** Other upcharge counted in GP: charged to the customer with no (or no measurable) cost — JF 2026-10-13 */
  otherGp?: string | number;
  /** other upcharges, row by row (JF 2026-10-13): charged to the customer with no actual cost; when set, other / otherGp are not used */
  upcharges?: Upcharge[];
  duty: string;
  sea: string;
  air: string;
  /** the customer's target price per piece, USD (JF 2026-10-13: shown in the style group / its Excel); targetCny = as typed in RMB */
  target?: string;
  targetCny?: string;
  sketch: string;
  origin: string;
  bomWarn: string;
  from: "ai" | "bom";
  /** `run` = the tracked AI estimate this sheet came from (admin page), so an export can be linked to it. */
  /** `base` = snapshot (JSON) of the last full AI pricing, for "reset to the AI result" (JF 2026-10-09). */
  ai: { items: Items; refs: string[]; demo: boolean; sub: string; open?: boolean; run?: string; base?: string } | null;
}

/* ---------------- AI checklist / pricing ---------------- */

export type ItemStatus = "got" | "assumed" | "ask" | "answered" | "missing";
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Item = { value?: string; status?: ItemStatus | string; note?: string; [k: string]: any };
export type Items = Record<string, Item>;

export interface QuestionOption { v: string; label_en?: string; label_zh?: string }
export interface Question { key: string; q_en?: string; q_zh?: string; options?: QuestionOption[] }
export interface ExtractResult {
  style?: { customer?: string; style_no?: string; style_name?: string; season?: string };
  items: Items;
  questions?: Question[];
}
export type Answer = { v: string; label: string } | null;

export interface PriceFabric { name?: string; position?: string; quality?: string; supplier?: string; unit?: string; fab_cat?: string; px?: number; yield?: number; basis?: string }
export interface PriceTrim { name?: string; position?: string; quality?: string; supplier?: string; trim_cat?: string; px?: number; yield?: number; basis?: string }
export interface PriceResult {
  sub?: string;
  fabrics?: PriceFabric[];
  trims?: PriceTrim[];
  sam?: number;
  city?: string;
  cm_base?: number;
  /** cm_base split into cutting / sewing / packing, same units (JF 2026-10-13) */
  cm_split?: { cut?: number; sew?: number; pack?: number };
  rush_pct?: number;
  cm_reason_en?: string;
  cm_reason_zh?: string;
  /** per-piece processes the factory arranges — part of the CM (JF 2026-10-09) */
  process?: { wash?: number; emb?: number; other?: number };
  process_desc?: { wash?: string; emb?: string; other?: string };
  /** older replies: washing / embroidery priced separately — now also put into the CM by default */
  additional?: { wash?: number; emb?: number };
  additional_desc?: string;
  testing_total?: number;
  inspection_total?: number;
  ref_scs?: string[];
  /** tiered quote: one judgement per requested quantity (JF 2026-10-12) */
  tiers?: PriceTier[];
}
